import * as React from 'react';
import {
  createErrorFilteringMiddleware,
  EntityForm,
  EntityDetailsContext,
  type EntityDetailsContextType,
} from '@web-config-app/entity-form';
import {
  useConfigUserFeedback,
  useAddConfigMessage,
  useAdminToolsSchemaSubmit,
  useConfigApp,
  useAuthoringEnvironments,
  useEntityOperationError,
  useConfigErrorBanner,
  useSchemaFormValidation,
} from '@web-config-app/core-react';
import { defaultRenderers } from '@web-config-app/entity-form-control-renderers';
import {
  Box,
  Button,
  StackLayout,
  StatusBanner,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  type AnnotatedJsonSchema,
  type AdminToolsSchemaPage,
  type EnvironmentKey,
} from '@web-config-app/core';
import {
  computeSchema,
  applyConditionalLogic,
} from '@web-config-app/schema-utils';
import {
  bannerAnimation,
  ErrorBanner,
  SuccessMessage,
} from '@web-config-app/core-react-ui';

export interface AdminToolsSchemaPageProps {
  config: AdminToolsSchemaPage;
}

/**
 * Renders an admin schema page.
 *
 * Responsibilities:
 * - Renders `EntityForm` from `config.schema` (plus optional environment selector)
 * - Builds JSON:API submit payload: `{ data: { type, attributes } }`
 * - POSTs to `config.endpoint.path` using the selected authoring environment
 * - Shows success/error feedback via the config user feedback store
 *
 * - If `config.enableEnvironmentSelection` is true, an `environment` field is injected
 *   into the form schema at runtime.
 * - The injected `environment` field selects which environment-scoped fetch
 *   client is used (`useEnvironmentFetch(selectedEnvironment)`).
 * - If `config.enableEnvironmentSelection` is false/omitted, no selector is
 *   injected and the request uses the base authoring environment from tenant
 *   context.
 * - The request still posts to the configured `config.endpoint.path`; only the
 *   environment context/base URL changes, and `environment` is omitted from
 *   submitted JSON:API `attributes`.
 */
export const AdminToolsSchemaPageComponent: React.FC<
  AdminToolsSchemaPageProps
> = ({ config }) => {
  const { formatMessage } = useIntl();
  const { defaultLocale, frontendValidation } = useConfigApp();
  const { baseAuthoringEnvironment, environmentMap } =
    useAuthoringEnvironments();
  const allowedUriProtocols = frontendValidation?.uriSchemePolicy?.allowed;
  // Initial form data. Seed only properties that declare an explicit `default`
  // so a control like a `default: true` boolean renders in its default state
  // and submits that value. JSON Forms does not apply schema `default`s on its
  // own here (the AJV instance is created without `useDefaults`), so without
  // this a `default: true` checkbox would render unchecked and the value would
  // be omitted from the payload until toggled.
  //
  // Seed ONLY explicit defaults — do not fall back to empty arrays/objects for
  // undefaulted properties. Seeding an optional array as `[]` would make it
  // "present" and defeat a `requiredIf` constraint (AJV's `required` only
  // checks presence), and would submit empty arrays for untouched fields.
  const getInitialData = React.useCallback(
    (): Record<string, unknown> =>
      Object.fromEntries(
        Object.entries(config.schema.properties ?? {})
          .filter(([, propertySchema]) => propertySchema.default !== undefined)
          .map(([key, propertySchema]) => [key, propertySchema.default]),
      ),
    [config.schema],
  );

  const [data, setData] =
    React.useState<Record<string, unknown>>(getInitialData);

  const addConfigMessage = useAddConfigMessage();

  // Build schema with dynamic environment enum values if needed
  const schemaWithEnvironment = React.useMemo<AnnotatedJsonSchema>(() => {
    if (!config.enableEnvironmentSelection) {
      return config.schema;
    }

    const hasEnvironmentSchemaCollision = Object.hasOwn(
      config.schema.properties ?? {},
      'environment',
    );

    if (hasEnvironmentSchemaCollision) {
      throw new Error(
        `AdminTools schema collision: "environment" is reserved when enableEnvironmentSelection is true (page: "${config.path}"). Remove schema.properties.environment from this page config.`,
      );
    }

    const environments = environmentMap
      ? Array.from(environmentMap.values())
      : [baseAuthoringEnvironment];

    const environmentEnumValues = environments.map((env) => env.environmentKey);
    const environmentEnumLabels = environments.map((env) => env.label);

    return {
      ...config.schema,
      properties: {
        environment: {
          type: 'string',
          title: 'Environment',
          'x-entity-label': {
            key: 'ENVIRONMENT',
          },
          enum: environmentEnumValues,
          'x-entity-enum-labels': environmentEnumLabels,
        },
        ...config.schema.properties,
      },
      required: ['environment', ...(config.schema.required || [])],
    };
  }, [
    config.schema,
    config.enableEnvironmentSelection,
    config.path,
    environmentMap,
    baseAuthoringEnvironment,
  ]);

  // Apply x-entity-conditional (omitIf / requiredIf / includeIf) against the
  // current form data. EntityForm hands the schema straight to JSON Forms,
  // which does not understand these custom annotations, so — unlike the
  // entity-detail flow — nothing evaluates them here unless we compute the
  // schema ourselves. Without this, conditional fields never hide and
  // requiredIf never takes effect on admin schema pages.
  const computedSchema = React.useMemo<AnnotatedJsonSchema>(
    () =>
      computeSchema(schemaWithEnvironment, data, [applyConditionalLogic], {
        recursive: false,
        // `$`-prefixed conditional paths resolve against the form root.
        rootData: data,
      }),
    [schemaWithEnvironment, data],
  );

  // Only submit attributes that survive the conditional computation: a field
  // hidden by omitIf must not be sent even if it still holds a seeded or stale
  // value in the form data (e.g. domainEntityIds when Backfill All is checked).
  const visibleData = React.useMemo<Record<string, unknown>>(() => {
    const visibleKeys = new Set(Object.keys(computedSchema.properties ?? {}));
    return Object.fromEntries(
      Object.entries(data).filter(([key]) => visibleKeys.has(key)),
    );
  }, [data, computedSchema]);

  const {
    configMessages,
    resetConfigMessages,
    removeConfigMessage,
    resetConfigUserFeedbackStore,
  } = useConfigUserFeedback((state) => ({
    configMessages: state.configMessages,
    resetConfigMessages: state.resetConfigMessages,
    removeConfigMessage: state.removeConfigMessage,
    resetConfigUserFeedbackStore: state.resetConfigUserFeedbackStore,
  }));

  const {
    showErrorBanner,
    hideErrorBanner,
    errorBannerTitle,
    errorBannerContents,
    showFormValidation,
    filteredErrorKeywords,
  } = useConfigErrorBanner(formatMessage({ id: config.labelKey }));

  const handleEntityOperationError = useEntityOperationError();

  const { validate } = useSchemaFormValidation({
    schema: computedSchema,
    data,
  });

  const successMessages =
    configMessages?.filter((message) => message.type === 'success') ?? [];

  const entityFormMiddleware = React.useMemo(
    () => createErrorFilteringMiddleware(filteredErrorKeywords ?? []),
    [filteredErrorKeywords],
  );

  const entityDetailsContextValue = React.useMemo(
    () =>
      ({
        currentAuthoringEnvironment:
          (data.environment as EnvironmentKey) ||
          baseAuthoringEnvironment.environmentKey,
        rootSchema: schemaWithEnvironment,
        entityRootData: data,
        formPath: '',
        isUsingConfigAppBackend: true,
      } as EntityDetailsContextType),
    [data, baseAuthoringEnvironment.environmentKey, schemaWithEnvironment],
  );

  const submitMutation = useAdminToolsSchemaSubmit({
    environment:
      (data.environment as EnvironmentKey) ||
      baseAuthoringEnvironment.environmentKey,
    data: visibleData,
    endpoint: config.endpoint,
    options: {
      onSuccess: () => {
        addConfigMessage({
          text: formatMessage({ id: config.successMessageKey }),
          type: 'success',
        });
        setData(getInitialData());
      },
      onError: (error) => {
        handleEntityOperationError(error);
      },
    },
  });

  const handleSubmit = () => {
    resetConfigUserFeedbackStore();

    if (!validate()) {
      return;
    }

    submitMutation.mutate();
  };

  // Reset on unmount
  React.useEffect(
    () => () => {
      resetConfigUserFeedbackStore();
    },
    [resetConfigUserFeedbackStore],
  );

  return (
    <Box css={{ maxWidth: '45rem', padding: '$two' }}>
      {showErrorBanner && (
        <ErrorBanner
          title={errorBannerTitle}
          onDismissClick={hideErrorBanner}
          defaultLocale={defaultLocale}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...errorBannerContents}
        />
      )}
      {successMessages.length > 0 && (
        <StatusBanner
          status="success"
          title={formatMessage({ id: 'SUCCESS' })}
          css={{ animation: bannerAnimation }}
          onDismissClick={() => resetConfigMessages()}
        >
          <Box
            as="ul"
            css={{
              marginBlock: 0,
              listStyleType: 'revert',
            }}
          >
            {successMessages.map((message) => (
              <SuccessMessage
                key={message.id}
                removeConfigMessage={removeConfigMessage}
                message={message}
              />
            ))}
          </Box>
        </StatusBanner>
      )}
      <EntityDetailsContext.Provider value={entityDetailsContextValue}>
        <StackLayout orientation="vertical" spacing="$oneAndHalf">
          <EntityForm
            schema={computedSchema}
            data={data}
            onDataChange={setData}
            middleware={entityFormMiddleware}
            showValidation={showFormValidation}
            renderers={defaultRenderers}
            allowedUriProtocols={allowedUriProtocols}
          />
          <Box>
            <Button
              priority="primary"
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending
                ? formatMessage({ id: 'SUBMITTING' })
                : formatMessage({ id: config.submitLabelKey })}
            </Button>
          </Box>
        </StackLayout>
      </EntityDetailsContext.Provider>
    </Box>
  );
};
